'use client'

import VideoPlayer from '@/components/VideoPlayer'
import { urlForImage } from '@/sanity/lib/image'
import dynamic from 'next/dynamic'
import Link from 'next/link'

// import MuxPlayer from '@mux/mux-player-react'

const Image = dynamic(() => import('next/image'))

interface ProjectThumbProps {
	item: any
	filter?: string
	thumbClass: string
}

const getThumbPadding = (previewMedia) => {
	// Padding for images
	if (previewMedia?.type === 'image') {
		const asset = previewMedia?.image?.asset
		if (asset?.metadata?.dimensions?.height && asset?.metadata?.dimensions?.width) {
			const aspectRatio = asset.metadata.dimensions.height / asset.metadata.dimensions.width
			return aspectRatio * 100
		}
	}
	// Padding for videos
	else {
		const aspectRatio = previewMedia?.video?.asset?.data?.aspect_ratio
		if (aspectRatio) {
			const [width, height] = aspectRatio.split(':')
			return (height / width) * 100
		}
	}

	return 100
}

export default function ProjectThumb({ item, filter, thumbClass }: ProjectThumbProps) {
	const { project, widthInGrid } = item
	const isVisible = filter ? project?.disciplines?.some((discipline: any) => discipline.slug.current === filter) : true

	const thumbPadding = getThumbPadding(project?.previewMedia)
	const { title, headline } = project

	const pendingVisibility = isVisible === null
	const visibilityClass = pendingVisibility ? 'opacity-0' : !isVisible ? 'opacity-30 grayscale scale-50' : ''
	let colSpanClass = 'w-full'
	let sizesAttr = '100vw'

	if (widthInGrid === '4') {
		colSpanClass += ` sm:site-grid-span-4`
		sizesAttr = '(min-width: 712px) 33vw, ' + sizesAttr
	}

	if (widthInGrid === '5') {
		colSpanClass += ' sm:site-grid-span-5'
		sizesAttr = '(min-width: 712px) 42vw, ' + sizesAttr
	}

	if (widthInGrid === '7') {
		colSpanClass += ' sm:site-grid-span-7'
		sizesAttr = '(min-width: 712px) 60vw, ' + sizesAttr
	}

	return (
		<div
			data-active={isVisible}
			className={`${colSpanClass} transition-all ${thumbClass}`}
			style={{
				scrollMarginTop: 'calc(var(--header-height) * 1.5)'
			}}>
			<Link href={`/work/${project?.slug?.current}`} className={`group block relative ${visibilityClass}`}>
				<div
					className="relative w-full rounded-weak overflow-clip"
					style={{
						paddingTop: `${thumbPadding}%`
					}}>
					{project?.previewMedia?.type === 'image' && (
						<Image
							className={`block w-full h-full absolute top-0 left-0 group-hover:scale-[1.02]`}
							style={{
								transition: 'transform 300ms ease'
							}}
							src={urlForImage(project?.previewMedia?.image).width(1920).url()}
							width={project?.previewMedia?.image?.asset?.metadata?.dimensions?.width}
							height={project?.previewMedia?.image?.asset?.metadata?.dimensions?.height}
							sizes={sizesAttr}
							alt={project?.previewMedia?.image?.alt || title}
							placeholder="blur"
							blurDataURL={project?.previewMedia?.image?.asset?.metadata?.lqip}
							unoptimized={project?.previewMedia?.image?.asset?.url?.includes('.gif') ? true : false} // mark Gif images as unoptimized (Next.js does not support Gif optimisation yet)
						/>
					)}

					{project?.previewMedia?.type === 'video' && project?.previewMedia?.video?.asset?.playbackId && (
						<div
							className="absolute w-full h-full top-0 left-0 overflow-hidden"
							style={
								{
									// backgroundColor: 'black'
								} as React.CSSProperties
							}>
							{/* <MuxVideo
								streamType="on-demand"
								playbackId={project?.previewMedia?.video?.asset?.playbackId}
								poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
								className="block w-full h-full absolute top-0 left-0 group-hover:scale-[1.02]"
								style={
									{
										'--media-object-fit': 'cover',
										transition: 'transform 300ms ease',
										// backgroundColor: 'black'
									} as React.CSSProperties
								}
								playsInline
								autoPlay
								muted
								loop
							/> */}
							<VideoPlayer
								playbackId={project?.previewMedia?.video?.asset?.playbackId}
								className="block w-full h-full absolute top-0 left-0 group-hover:scale-[1.02] transition-transform"
								autoplay
								loop
								showAudioToggle={false}
							/>
						</div>
					)}
				</div>
				<p className="style-body--lg md:style-body--xl mt-5 mb-1">{title}</p>
				<p className="italic text-black md:style-body--lg">{headline}</p>
			</Link>
		</div>
	)
}
